import { useTranslation } from "react-i18next";
import { Control, Controller, useController, useForm } from "react-hook-form";
import { useToaster } from "@hooks/useToaster";
import { SearchRequest } from "@services/travel";
import { ConnectError } from "@connectrpc/connect";
import { Button, Calendar, Heading, Icon, Select } from "@thetreep/ui";
import { useLoadingEffect } from "@hooks/useLoadingEffect";
import { DAP, DAPStatus } from "@gen/dap/v1/dap_pb";
import { useState, useEffect, useMemo } from "react";
import { SearchLoader } from "@pages/booking/search/SearchLoader";
import { useNavigate, useParams } from "react-router";
import { routes } from "@routes";
import {
  LocalDateTimeSchema,
  PlaneCabinClass,
  TrainCabinClass,
} from "@gen/travel/v1/common_pb";
import { searchExchange } from "@services/exchange";
import { useExchangeContext } from "./context";
import { ExchangeHeader } from "./ExchangeHeader";
import theme from "@theme";
import { FormButton } from "../search/TravelForm";
import { getDateLocale } from "@utils/locale";
import { TimeSelect } from "../search/TimeSelect";
import { Place } from "@gen/place/v1/place_pb";
import { formatCoordinates } from "@utils/coordinates";
import { SearchDate } from "./SearchDate";
import { localDateTimeToDate } from "@utils/date";
import { create } from "@bufbuild/protobuf";
export type CabinClass = TrainCabinClass | PlaneCabinClass;

export function ExchangeSearch() {
  // deps
  const { t } = useTranslation();
  const { addToast } = useToaster();
  const navigate = useNavigate();
  const { dap, travel, journey } = useExchangeContext();
  const { travelId, journeyId } = useParams();

  // params
  const { origin, destination, way } = useMemo(() => {
    if (travel?.outward?.id === journey?.id) {
      return {
        origin: dap.origin,
        destination: dap.destination,
        way: "outward",
      };
    } else {
      return {
        origin: dap.destination,
        destination: dap.origin,
        way: "inward",
      };
    }
  }, [journey]);

  // form
  const { control, reset, getValues } = useForm<SearchRequest>({
    defaultValues: {},
  });

  useEffect(() => {
    reset({
      outwardDate: {
        date: travel.outward?.schedule?.departure
          ? localDateTimeToDate(travel.outward?.schedule?.departure)
          : new Date(),
        type: "departure",
      },
      inwardDate: {
        date: travel.inward?.schedule?.arrival
          ? localDateTimeToDate(travel.inward?.schedule?.arrival)
          : new Date(),
        type: "departure",
      },
    });
  }, [travel]);

  // state
  const [isLoading, setIsLoading] = useState<boolean>(false);
  useLoadingEffect(isLoading);

  async function onSubmit() {
    if (!travelId || !journeyId) return;
    const values = getValues();
    const date =
      travel.outward?.id === journey.id
        ? values.outwardDate
        : values.inwardDate;

    if (
      !travel ||
      !travel.outward ||
      !travel.inward ||
      !values.outwardDate ||
      !date
    )
      return;

    try {
      setIsLoading(true);
      const response = await searchExchange(travel?.id, journey?.id, date);
      navigate(
        routes.EXCHANGE_RESULTS.replace(":travelId", travelId).replace(
          ":journeyId",
          journeyId,
        ),
        {
          state: { searchResponse: response },
        },
      );
    } catch (e) {
      if (e instanceof ConnectError && e.rawMessage) {
        addToast("error", e.rawMessage);
      } else {
        addToast("error", t("genericError"));
      }
    } finally {
      setIsLoading(false);
    }
  }

  function canBook(dap: DAP): boolean {
    return (
      !!dap &&
      dap.status == DAPStatus.DAP_STATUS_VALID &&
      dap.isBookableByCaller
    );
  }

  function displayPlace(p: Place | undefined): string {
    if (!p) {
      return "";
    }
    return p.name || formatCoordinates(p.latitude || 0, p.longitude || 0);
  }

  if (!travel || !travel.outward || !travel.inward) return null;

  return (
    <>
      <div className="flex flex-col items-center justify-center bg-cnam-light-100 w-full h-full pb-12 mt-10 lg:mt-0">
        {isLoading && <SearchLoader type={"outward"} />}
        <div className="flex flex-col gap-10 w-full">
          <div className="flex flex-col gap-4">
            <h1 className="text-cnam-blue font-jakarta text-[32px] md:text-[40px]">
              {t("booking.exchange.title")}
            </h1>
            <p className="text-base font-jakarta text-[20px]">
              {t("booking.exchange.subtitle")}
            </p>
          </div>

          <ExchangeHeader />
          {dap && canBook(dap) && (
            <>
              <div
                className="
            w-full
            bg-neutral-50 p-8 rounded-md
            flex flex-col gap-2"
              >
                <Heading>{t("travel")}</Heading>
                <div className="flex items-center gap-2 text-grey-blue-500 text-xs">
                  <Icon
                    src="information_line"
                    size={16}
                    color={theme.colors["grey-blue"][500]}
                  />
                  <span className="mt-1">{t("search.travelFormInfo")}</span>
                </div>

                <div className="border-t border-light-blue-200 my-6"></div>

                <div className="grid grid-cols-1 md:grid-cols-[1fr_420px] gap-6 h-fit">
                  <div className="flex flex-col gap-2">
                    <span className="font-jakarta text-xl text-neutral-900 my-2">
                      {t("search.originDestination")}
                    </span>
                    <FormButton
                      icon="location_line"
                      prefix={`${t("from")} ${displayPlace(origin)}`}
                      disabled
                    />
                    <FormButton
                      icon="location_line"
                      prefix={`${t("to")} ${displayPlace(destination)}`}
                      disabled
                    />

                    <SearchDate control={control} way={way} />

                    <div className="flex items-center justify-between">
                      <span className="font-jakarta text-xl text-neutral-900 my-2">
                        {t("search.passengers")}
                      </span>
                      <div className="flex items-center gap-2">
                        <Icon
                          src="group_line"
                          size={16}
                          color={theme.colors["blue-medium"].DEFAULT}
                        />
                        <span className="text-blue-medium text-sm">
                          {travel.passengers?.length || 0}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center justify-center md:h-[449px]">
                    <CalendarPanel
                      control={control}
                      name={way === "outward" ? "outwardDate" : "inwardDate"}
                      minDate={
                        way === "outward"
                          ? new Date()
                          : localDateTimeToDate(
                              travel.outward.schedule?.departure ||
                                create(LocalDateTimeSchema),
                            )
                      }
                      maxDate={
                        way === "outward"
                          ? localDateTimeToDate(
                              travel.inward.schedule?.departure ||
                                create(LocalDateTimeSchema),
                            )
                          : undefined
                      }
                    />
                  </div>
                </div>
              </div>
              <div className="w-full md:self-end md:w-fit">
                <Button
                  data-testid="search-submit"
                  type="submit"
                  width="100%"
                  onClick={() => onSubmit()}
                  endEnhancer={<Icon src="arrow_right_line" color="white" />}
                >
                  {t("search.search")}
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}

interface CalendarPanelProps {
  control: Control<SearchRequest>;
  name: "outwardDate" | "inwardDate";
  minDate?: Date;
  maxDate?: Date;
}

function CalendarPanel({
  control,
  name,
  minDate,
  maxDate,
}: CalendarPanelProps) {
  const { t } = useTranslation();
  const { field } = useController({
    control,
    name: name === "outwardDate" ? "outwardDate.date" : "inwardDate.date",
  });

  return (
    <div
      className="
            flex flex-col items-center justify-center w-fit
            border border-grey-blue-300 rounded-xl px-4 bg-neutral-50"
    >
      <Calendar
        value={[field.value]}
        density="high"
        locale={getDateLocale()}
        onChange={(e) => {
          const d = e.date;
          if (d instanceof Date) {
            const value = field.value;
            field.onChange(
              new Date(
                d.getFullYear(),
                d.getMonth(),
                d.getDate(),
                value?.getHours() || 8,
                value?.getMinutes() || 0,
              ),
            );
          }
        }}
        minDate={minDate}
        maxDate={maxDate}
        overrides={{
          Day: {
            style: ({
              $selected,
              $pseudoSelected,
              $pseudoHighlighted,
              $isHovered,
            }) => ({
              ...($selected && {
                ":after": {
                  borderTopColor: theme.colors.cnam.blue.DEFAULT,
                  borderBottomColor: theme.colors.cnam.blue.DEFAULT,
                  borderRightColor: theme.colors.cnam.blue.DEFAULT,
                  borderLeftColor: theme.colors.cnam.blue.DEFAULT,
                  backgroundColor: theme.colors.cnam.blue.DEFAULT,
                },
              }),
              ...(($pseudoSelected ||
                $selected ||
                $pseudoHighlighted ||
                $isHovered) && {
                ":before": {
                  backgroundColor: theme.colors["light-blue"][200],
                },
              }),
            }),
          },
          MonthYearSelectButton: {
            props: {
              "data-testid": "search-date-select",
            },
          },
        }}
      />
      <div className="flex justify-between w-full gap-6 mb-4 [&_select]:p-2">
        <Controller
          name="outwardDate.type"
          control={control}
          render={({ field }) => (
            <Select
              options={[
                {
                  label: t("search.leaveAt"),
                  value: "departure",
                },
                {
                  label: t("search.arriveAt"),
                  value: "arrival",
                },
              ]}
              value={field.value}
              onChange={field.onChange}
            />
          )}
        />
        <TimeSelect control={control} name={name} />
      </div>
    </div>
  );
}
